import { resilientPromise } from '@citrite/workspace-ui-platform';
import { trackAnalyticsEvent } from 'analytics';
import { v4 as uuid } from 'uuid';
import {
	ChromeAppErrorReason,
	ChromeAppMessage,
	ChromeAppMessageType,
	ChromeAppResponse,
	ChromeAppWrapperMessage,
	ExtensionCommunicationAdapter,
} from 'Environment/ChromeApp/types';
import { ChromeAppEventPayload } from 'Workspace/TelemetryEvents/chromeApp/chromeAppCASReporter';

function handleChromeAppResponseForTimeout(
	adapter: ExtensionCommunicationAdapter,
	workload: any,
	type: ChromeAppMessageType,
	timeout: number
) {
	let chromeAppResponseTimeout: ReturnType<typeof setTimeout> = undefined;
	function timeoutPromiseWrapper() {
		return new Promise<ChromeAppResponse>((_resolve, reject) => {
			chromeAppResponseTimeout = setTimeout(() => {
				const error = new Error(`ChromeApp response timeout for ${type}`);
				error.name = ChromeAppErrorReason.TimeOut;
				trackAnalyticsEvent(
					ChromeAppEventPayload.postMessageError({
						messageType: type,
						correlationId: workload?.correlationId,
						errorMessage: error.message,
					})
				);
				reject(error);
			}, timeout);
		});
	}

	return Promise.race([adapter(workload, type), timeoutPromiseWrapper()])
		.then(response => {
			trackAnalyticsEvent(
				ChromeAppEventPayload.postMessageResponse({
					messageType: type,
					correlationId: workload?.correlationId,
				})
			);
			return response;
		})
		.catch(error => {
			if (error?.name !== ChromeAppErrorReason.TimeOut) {
				trackAnalyticsEvent(
					ChromeAppEventPayload.postMessageError({
						messageType: type,
						correlationId: workload?.correlationId,
						errorMessage: error?.message,
					})
				);
			}
			return Promise.reject(error);
		})
		.finally(() => {
			clearTimeout(chromeAppResponseTimeout);
		});
}

function sendMessageToChromeAppWithTimeOut(
	adapter: ExtensionCommunicationAdapter,
	workload: any,
	type: ChromeAppMessageType,
	timeout: number
) {
	if (timeout) {
		return handleChromeAppResponseForTimeout(adapter, workload, type, timeout);
	}

	return adapter(workload, type);
}

export function sendMessageToChromeAppWrapper(
	adapter: ExtensionCommunicationAdapter,
	message: ChromeAppWrapperMessage
) {
	const { workload, type, timeout, retries = 0 } = message;
	return resilientPromise(
		() => sendMessageToChromeAppWithTimeOut(adapter, workload, type, timeout),
		{ maxAttempts: retries + 1 }
	);
}

export function createChromeAppMessage(workload: any, type: ChromeAppMessageType) {
	const message: ChromeAppMessage = {
		header: {
			type,
		},
		payload: {
			...workload,
			correlationId: uuid(),
		},
	};
	return message;
}

export function isChromeAppError(error: Error): boolean {
	return (
		error &&
		Object.values(ChromeAppErrorReason).includes(error.name as ChromeAppErrorReason)
	);
}
